/**
 * Pathway Detail Page
 *
 * Shows the modules within a learning pathway and user progress
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  ArrowLeft,
  GraduationCap,
  Target,
  CheckCircle,
  Lock,
  Play,
  Trophy,
  Clock,
  ArrowRight,
  BookOpen,
  Award,
  MessageSquare,
  Users,
  FileText,
  HelpCircle,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

interface PathwayModule {
  id: string;
  pathway_id: string;
  sequence_order: number;
  title: string;
  description: string | null;
  module_type: string;
  coaching_template_id: string | null;
  simulation_agent_id: string | null;
  unlock_score: number;
  is_required: boolean;
  estimated_minutes: number | null;
  // Joined data
  coaching_templates?: { name: string } | null;
  simulation_agents?: { name: string; type: string; image_url?: string } | null;
}

interface ModuleProgress {
  id: string;
  module_id: string;
  status: string;
  best_score: number | null;
  attempts: number;
  completed_at: string | null;
}

export default function PathwayDetailPage() {
  const { pathwayId } = useParams<{ pathwayId: string }>();
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const queryClient = useQueryClient();


  // Fetch pathway details
  const { data: pathway, isLoading: pathwayLoading } = useQuery({
    queryKey: ['pathway-detail', pathwayId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('learning_pathways')
        .select('*')
        .eq('id', pathwayId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!pathwayId
  });

  // Fetch pathway modules
  const { data: modules, isLoading: modulesLoading } = useQuery({
    queryKey: ['pathway-modules', pathwayId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pathway_modules')
        .select(`
          *,
          coaching_templates (name),
          simulation_agents (name, type, image_url)
        `)
        .eq('pathway_id', pathwayId)
        .order('sequence_order');

      if (error) throw error;
      return data as PathwayModule[];
    },
    enabled: !!pathwayId
  });

  // Fetch user enrollment - use maybeSingle to avoid 406 errors
  const { data: enrollment, isLoading: enrollmentLoading } = useQuery({
    queryKey: ['pathway-enrollment', pathwayId, profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_pathway_enrollments')
        .select('*')
        .eq('pathway_id', pathwayId)
        .eq('user_id', profile?.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!pathwayId && !!profile?.id
  });

  // Fetch module progress
  const { data: moduleProgress, isLoading: progressLoading } = useQuery({
    queryKey: ['module-progress', enrollment?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_progress')
        .select('*')
        .eq('enrollment_id', enrollment?.id);

      if (error) throw error;
      return data as ModuleProgress[];
    },
    enabled: !!enrollment?.id
  });

  // Enroll mutation
  const enrollMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Not authenticated');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated with Supabase. Please refresh and try again.');

      const { data: newEnrollment, error: enrollError } = await supabase
        .from('user_pathway_enrollments')
        .insert({
          user_id: profile.id,
          pathway_id: pathwayId,
          company_id: user?.company_id ?? null,
          status: 'enrolled'
        })
        .select()
        .single();

      if (enrollError) throw enrollError;

      // Create module progress records
      if (modules && modules.length > 0) {
        const progressRecords = modules.map((module, index) => ({
          enrollment_id: newEnrollment.id,
          module_id: module.id,
          user_id: profile.id,
          status: index === 0 ? 'unlocked' : 'locked'
        }));

        await supabase.from('module_progress').insert(progressRecords);

        // Update current module
        await supabase
          .from('user_pathway_enrollments')
          .update({ current_module_id: modules[0].id, status: 'in_progress' })
          .eq('id', newEnrollment.id);
      }

      return newEnrollment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pathway-enrollment', pathwayId] });
      queryClient.invalidateQueries({ queryKey: ['module-progress'] });
      toast.success('Successfully enrolled!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to enroll');
    }
  });

  const getModuleProgress = (moduleId: string): ModuleProgress | undefined => {
    return moduleProgress?.find(p => p.module_id === moduleId);
  };

  const isModuleUnlocked = (module: PathwayModule, index: number): boolean => {
    if (!enrollment) return false;
    if (index === 0) return true;

    const progress = getModuleProgress(module.id);
    if (progress?.status === 'completed' || progress?.status === 'unlocked') return true;

    // Check if previous module is completed with sufficient score
    const prevModule = modules?.[index - 1];
    if (prevModule) {
      const prevProgress = getModuleProgress(prevModule.id);
      if (prevProgress?.status === 'completed') {
        return (prevProgress.best_score || 0) >= module.unlock_score;
      }
    }

    return false;
  };

  const handleStartModule = (module: PathwayModule) => {
    // Store pathway context
    sessionStorage.setItem('pathway_context', JSON.stringify({
      pathwayId,
      moduleId: module.id,
      pathwayName: pathway?.name
    }));

    if (module.module_type === 'coaching') {
      if (!module.simulation_agent_id) {
        toast.error('This coaching module has not been configured yet. Please contact your administrator.');
        return;
      }
      // Always use agent-based coaching route (template is inside the agent)
      navigate(`/coaching/${module.simulation_agent_id}/session?pathway=${pathwayId}&module=${module.id}`);
    } else if (module.module_type === 'simulation' || module.module_type === 'practice') {
      if (!module.simulation_agent_id) {
        toast.error('This simulation module has not been configured yet. Please contact your administrator.');
        return;
      }
      navigate(`/simulation/${module.simulation_agent_id}?pathway=${pathwayId}&module=${module.id}`);
    } else if (module.module_type === 'debrief') {
      if (!module.simulation_agent_id) {
        toast.error('This debrief module has not been configured yet. Please contact your administrator.');
        return;
      }
      // Always use agent-based coaching route (template is inside the agent)
      navigate(`/coaching/${module.simulation_agent_id}/session?pathway=${pathwayId}&module=${module.id}&debrief=true`);
    } else if (module.module_type === 'assessment') {
      if (!module.simulation_agent_id) {
        toast.error('This assessment module has not been configured yet. Please contact your administrator.');
        return;
      }
      navigate(`/simulation/${module.simulation_agent_id}?pathway=${pathwayId}&module=${module.id}&assessment=true`);
    } else if (module.module_type === 'reading') {
      navigate(`/learning/reading/${module.id}?pathway=${pathwayId}`);
    } else if (module.module_type === 'quiz') {
      navigate(`/learning/quiz/${module.id}?pathway=${pathwayId}`);
    } else {
      toast.error('Unknown module type');
    }
  };

  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'coaching': return BookOpen;
      case 'simulation': return Target;
      case 'practice': return Users;
      case 'debrief': return MessageSquare;
      case 'assessment': return Award;
      case 'reading': return FileText;
      case 'quiz': return HelpCircle;
      default: return BookOpen;
    }
  };

  const getModuleStatusBadge = (module: PathwayModule, index: number) => {
    const progress = getModuleProgress(module.id);
    const unlocked = isModuleUnlocked(module, index);

    if (progress?.status === 'completed') {
      return <Badge className="bg-green-500">Completed</Badge>;
    }
    if (progress?.status === 'in_progress') {
      return <Badge className="bg-blue-500">In Progress</Badge>;
    }
    if (unlocked) {
      return <Badge variant="outline">Ready</Badge>;
    }
    return <Badge variant="secondary">Locked</Badge>;
  };

  const isLoading = pathwayLoading || modulesLoading || enrollmentLoading || progressLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!pathway) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Alert variant="destructive">
          <AlertDescription>Pathway not found</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/learning/pathways')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Pathways
        </Button>
      </div>
    );
  }

  const completedModules = moduleProgress?.filter(p => p.status === 'completed').length || 0;
  const totalModules = modules?.length || 0;
  const progressPercent = totalModules > 0 ? (completedModules / totalModules) * 100 : 0;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/learning/pathways')}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Pathways
      </Button>

      {/* Pathway Header */}
      <Card className="p-6 mb-8">
        <div className="flex items-start gap-6">
          <div className="p-4 bg-primary/10 rounded-xl">
            <GraduationCap className="w-10 h-10 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-2">{pathway.name}</h1>
                <p className="text-muted-foreground mb-4">{pathway.description}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="capitalize">{pathway.category}</Badge>
                  <Badge className="capitalize">{pathway.difficulty_level}</Badge>
                  {pathway.certification_name && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {pathway.certification_name}
                    </Badge>
                  )}
                  {pathway.estimated_duration_hours && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      ~{pathway.estimated_duration_hours}h
                    </Badge>
                  )}
                </div>
              </div>
              {!enrollment && (
                <Button
                  onClick={() => enrollMutation.mutate()}
                  disabled={enrollMutation.isPending}
                  size="lg"
                >
                  {enrollMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Enroll Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar (if enrolled) */}
        {enrollment && (
          <div className="mt-6 pt-6 border-t">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Your Progress</span>
              <span className="font-medium">{completedModules} of {totalModules} modules</span>
            </div>
            <Progress value={progressPercent} className="h-3" />
            {enrollment.status === 'completed' && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg flex items-center gap-3">
                <Trophy className="w-6 h-6 text-green-600" />
                <div>
                  <div className="font-medium text-green-800 dark:text-green-300">
                    Pathway Completed!
                  </div>
                  {pathway.certification_name && (
                    <div className="text-sm text-green-600 dark:text-green-400">
                      You've earned: {pathway.certification_name}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Modules List */}
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5" />
        Modules ({totalModules})
      </h2>

      <div className="space-y-4">
        {modules?.map((module, index) => {
          const progress = getModuleProgress(module.id);
          const unlocked = isModuleUnlocked(module, index);
          const Icon = getModuleIcon(module.module_type);
          const isCompleted = progress?.status === 'completed';

          return (
            <Card
              key={module.id}
              className={`p-5 transition-all ${
                !enrollment ? 'opacity-75' :
                !unlocked ? 'opacity-60' :
                isCompleted ? 'border-green-200 dark:border-green-800' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Module Number */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold
                  ${isCompleted ? 'bg-green-500 text-white' :
                    unlocked ? 'bg-primary text-white' :
                    'bg-muted text-muted-foreground'}
                `}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : unlocked ? (
                    index + 1
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                </div>

                {/* Module Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{module.title}</h3>
                        {getModuleStatusBadge(module, index)}
                      </div>
                      {module.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {module.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 capitalize">
                          <Icon className="w-3.5 h-3.5" />
                          {module.module_type}
                        </span>
                        {module.estimated_minutes && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            ~{module.estimated_minutes} min
                          </span>
                        )}
                        {module.unlock_score > 0 && index > 0 && (
                          <span className="flex items-center gap-1">
                            <Target className="w-3.5 h-3.5" />
                            Requires {module.unlock_score}+ on previous
                          </span>
                        )}
                        {progress?.best_score && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Trophy className="w-3.5 h-3.5" />
                            Best: {progress.best_score}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    <div>
                      {enrollment && unlocked && (
                        <Button
                          size="sm"
                          variant={isCompleted ? 'outline' : 'default'}
                          onClick={() => handleStartModule(module)}
                        >
                          {isCompleted ? (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              Redo
                            </>
                          ) : progress?.status === 'in_progress' ? (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              Continue
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              Start
                            </>
                          )}
                        </Button>
                      )}
                      {!enrollment && (
                        <Badge variant="outline">Enroll to start</Badge>
                      )}
                      {enrollment && !unlocked && (
                        <Badge variant="secondary">
                          <Lock className="w-3 h-3 mr-1" />
                          Locked
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {(!modules || modules.length === 0) && (
        <Card className="p-12 text-center">
          <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Modules Yet</h3>
          <p className="text-muted-foreground">
            This pathway is being prepared. Check back soon!
          </p>
        </Card>
      )}
    </div>
  );
}
